import axios from "axios";
import config from "../../../../config";
import { bffApi } from "../../../../services/bff/bffApi";
import {
  createHTMLElement,
  downloadFile,
} from "../../../../utils/downloadFiles";
import { BaseService } from "../../../../global/classes";
import { ApplySignatureDiscountsResult, PendingSignatureByMerchant } from "../types";

export default class PainelDePagamentosService extends BaseService {
  // Total pendente de assinaturas por merchant, de todas as competências — mesma base
  // usada pela planilha e pelo desconto aplicado no GCB.
  async getAssinaturasPendentes(): Promise<PendingSignatureByMerchant[]> {
    try {
      const response = await bffApi.get(
        `/api/gcb/v1/signaturemanagement/get-pending-signatures`
      );

      return response.data ?? [];
    } catch (error) {
      throw error;
    }
  }

  async getPagamentos(page: number, pageSize: number, filter: any) {
    const formattedFilter: any = {
      acquirer: filter.acquirer,
      anticipationIncluded: filter.anticipationIncluded,
      businessName: filter.businessName,
      merchantId: filter.merchantId,
      saleInitDate: filter.initialDate,
      saleFinishDate: filter.finalDate,
      centralizedLiquidationEnabled: filter.centralizedLiquidationEnabled,
      pageNumber: page + 1,
      pageSize,
    };

    if (filter.paid !== null && filter.paid !== undefined) formattedFilter.paid = filter.paid;

    if (filter.tipoPagamento && filter.tipoPagamento !== '0') formattedFilter.tipoPagamento = filter.tipoPagamento;

    if (formattedFilter.acquirer == -1) delete formattedFilter.acquirer;

    try {
      return bffApi.post(
        `/api/flux/v1/payments/query-paged`,
        { ...formattedFilter }
      );
    } catch (error) {
      throw error;
    }
  }

  async getPaymentsLog() {
    try {
      return axios.get(`${config.apiBaseUrl}/billings/get-payments-log`, {
        headers: {
          Authorization: `Bearer ${this.legacyAccessToken?.token}`,
        },
      });
    } catch (error) {
      throw error;
    }
  }

  async downloadPagamentos(type: string, filter: any) {
    // As assinaturas do arquivo vêm do próprio Flux (todo o pendente por merchant),
    // a mesma base que será debitada ao salvar os descontos.
    const formattedFilter: any = {
      acquirer: filter.acquirer,
      anticipationIncluded: filter.anticipationIncluded,
      businessName: filter.businessName,
      merchantId: filter.merchantId,
      saleInitDate: filter.initialDate,
      saleFinishDate: filter.finalDate,
      centralizedLiquidationEnabled: filter.centralizedLiquidationEnabled,
    };

    if (filter.paid !== null && filter.paid !== undefined) formattedFilter.paid = filter.paid;

    if (filter.tipoPagamento && filter.tipoPagamento !== '0') formattedFilter.tipoPagamento = filter.tipoPagamento;

    if (formattedFilter.acquirer == -1) delete formattedFilter.acquirer;

    const result = await bffApi.post(
      `/api/flux/v1/payments/download`,
      { ...formattedFilter },
      {
        responseType: "blob",
      }
    );

    try {
      const htmlElement = createHTMLElement(result.data);
      const fileName = "PainelDePagamentos";
      downloadFile(htmlElement, fileName, type);
    } catch (error) {
      throw error;
    }
  }

  async updatePagamentosExternos(filter: any, data: any, applyToAll?: any) {
    try {
      const updateBody: any = {
        saleInitDate: filter.initialDate,
        saleFinishDate: filter.finalDate,
      };
      if (filter.paid !== null && filter.paid !== undefined) updateBody.paid = filter.paid;
      if (filter.tipoPagamento && filter.tipoPagamento !== '0') updateBody.tipoPagamento = filter.tipoPagamento;

      if (applyToAll) {
        updateBody.applyToAll = applyToAll;
      } else {
        updateBody.merchants = (data as any[]).map((m: any) => ({
          merchant: m.merchant,
          pixPaid: m.pixPago ?? m.pixPaid,
          creditPaid: m.creditoPago ?? m.creditPaid,
          debitPaid: m.debitoPago ?? m.debitPaid,
        }));
      }
      await bffApi.post(
        `/api/flux/v1/payments/update-status`,
        updateBody
      );
    } catch (error) {
      throw error;
    }
  }

  // O Flux varre todos os merchants do filtro (sem paginação), calcula o desconto de
  // assinaturas de cada um e grava direto no GCB. Devolve o resumo do que foi aplicado.
  async applySignatureDiscounts(filter: any): Promise<ApplySignatureDiscountsResult> {
    const body: any = {
      saleInitDate: filter.initialDate,
      saleFinishDate: filter.finalDate,
      businessName: filter.businessName,
      merchantId: filter.merchantId,
      acquirer: filter.acquirer == -1 ? null : filter.acquirer,
      anticipationIncluded: filter.anticipationIncluded,
      centralizedLiquidationEnabled: filter.centralizedLiquidationEnabled,
    };

    if (filter.paid !== null && filter.paid !== undefined) body.paid = filter.paid;

    if (filter.tipoPagamento && filter.tipoPagamento !== '0') body.tipoPagamento = filter.tipoPagamento;

    try {
      const response = await bffApi.post(
        `/api/flux/v1/payments/signature-discounts`,
        body
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateSignatureLog(filter: any) {
    try {
      await axios.post(
        `${config.apiBaseUrl}/billings/save-payments-log`,
        JSON.stringify(filter.tipoPagamento),
        {
          headers: {
            Authorization: `Bearer ${this.legacyAccessToken?.token}`,
            "Content-Type": "application/json",
          },
        }
      );
    } catch (error) {
      throw error;
    }
  }

}
